// AddItemInput - input component for creating new items
// Implements US-3.1: Item CRUD and Drag-Drop Reordering

import React, { useState } from 'react';
import styles from './AddItemInput.module.css';

interface AddItemInputProps {
  onAddItem: (title: string) => Promise<void>;
  disabled?: boolean;
}

/**
 * RENDER add item input field
 * - Character counter (max 200 chars)
 * - Validation and error handling
 * - Optimistic UI feedback
 */
export const AddItemInput: React.FC<AddItemInputProps> = ({
  onAddItem,
  disabled = false,
}) => {
  const [title, setTitle] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const MAX_LENGTH = 200;

  /**
   * HANDLE input change
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_LENGTH) {
      setTitle(value);
      setError(null);
    }
  };

  /**
   * HANDLE form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // VALIDATE input
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Titel darf nicht leer sein');
      return;
    }

    if (trimmedTitle.length > MAX_LENGTH) {
      setError(`Titel darf maximal ${MAX_LENGTH} Zeichen lang sein`);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // CREATE item via parent callback
      await onAddItem(trimmedTitle);

      // CLEAR input on success
      setTitle('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Element konnte nicht hinzugefügt werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * HANDLE Enter key
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  const remainingChars = MAX_LENGTH - title.length;
  const isNearLimit = remainingChars < 20;

  return (
    <div className={styles.addItemContainer}>
      <form onSubmit={handleSubmit} className={styles.addItemForm}>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            className={styles.input}
            placeholder="Titel eingeben..."
            value={title}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled || isSubmitting}
            autoComplete="off"
          />
          <span
            className={`${styles.charCounter} ${isNearLimit ? styles.warning : ''}`}
          >
            {title.length}/{MAX_LENGTH}
          </span>
        </div>

        <button
          type="submit"
          className={styles.addButton}
          disabled={disabled || isSubmitting || !title.trim()}
        >
          {isSubmitting ? 'Füge hinzu...' : 'Hinzufügen'}
        </button>
      </form>

      {/* ERROR message */}
      {error && (
        <div className={styles.errorMessage}>
          <span className={styles.errorIcon}>⚠</span>
          {error}
        </div>
      )}
    </div>
  );
};
